import clsx from 'clsx';
import { type ChangeEvent, type FC, type KeyboardEvent, type ReactNode, useCallback, useRef, type Ref } from 'react';

import { WithClassName } from '../../../../types/react';

import styles from './Checkbox.module.scss';

const CheckGlyph: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path d="M5 12L10 17L20 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export type CheckboxLabelType = 'regular12' | 'regular14';

export type CheckboxProps = WithClassName & {
  inputId?: string;
  inputName?: string;
  inputRef?: Ref<HTMLInputElement>;
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  label?: ReactNode;
  labelType?: CheckboxLabelType;
  alignLabel?: 'center' | 'start';
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
};

export const Checkbox: FC<CheckboxProps> = (props) => {
  const {
    className,
    inputId,
    inputName,
    inputRef,
    checked,
    defaultChecked,
    disabled,
    label,
    labelType = 'regular14',
    alignLabel = 'center',
    onChange,
    onBlur,
  } = props;

  const inputNodeRef = useRef<HTMLInputElement | null>(null);

  const setInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      inputNodeRef.current = node;
      if (typeof inputRef === 'function') {
        inputRef(node);
      } else if (inputRef && 'current' in inputRef) {
        (inputRef as { current: HTMLInputElement | null }).current = node;
      }
    },
    [inputRef]
  );

  const handleToggle = useCallback(
    (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || disabled) {
        return;
      }

      e.preventDefault();
      inputNodeRef.current?.click();
    },
    [disabled]
  );

  return (
    <label className={clsx(styles.root, { [styles.disabled]: disabled }, className)}>
      <input
        className={styles.input}
        ref={setInputRef}
        id={inputId}
        name={inputName}
        type="checkbox"
        disabled={disabled}
        checked={checked}
        defaultChecked={defaultChecked}
        onChange={onChange}
        onBlur={onBlur}
      />

      <div className={styles.checkboxContainer}>
        <div className={styles.checkbox} tabIndex={0} onKeyDown={handleToggle}>
          {checked && <CheckGlyph className={styles.icon} />}
        </div>
      </div>

      {label && (
        <div
          className={clsx(
            styles.label,
            labelType === 'regular12' && styles.label_regular12,
            alignLabel === 'start' && styles.label_alignStart
          )}
        >
          {label}
        </div>
      )}
    </label>
  );
};
